import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  ClipboardCheck, Filter, RefreshCw, Users, Send, ChevronRight,
  AlertTriangle, CheckCircle2, Clock, MinusCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_ICONS = {
  overdue: <AlertTriangle className="w-3.5 h-3.5 text-red-500" />,
  due: <Clock className="w-3.5 h-3.5 text-amber-500" />,
  compliant: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />,
  excluded: <MinusCircle className="w-3.5 h-3.5 text-slate-400" />,
  declined: <MinusCircle className="w-3.5 h-3.5 text-red-400" />,
  unknown: <Clock className="w-3.5 h-3.5 text-blue-400" />,
};

const STATUS_COLORS = {
  overdue: 'bg-red-50 text-red-700 border-red-200',
  due: 'bg-amber-50 text-amber-700 border-amber-200',
  compliant: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  excluded: 'bg-slate-50 text-slate-500 border-slate-200',
  declined: 'bg-red-50 text-red-500 border-red-200',
  unknown: 'bg-blue-50 text-blue-600 border-blue-200',
};

export default function QualityRegistry() {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [presets, setPresets] = useState([]);
  const [activePreset, setActivePreset] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [measureFilter, setMeasureFilter] = useState('');
  const [measures, setMeasures] = useState([]);
  const [patients, setPatients] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [page, setPage] = useState(0);
  const limit = 30;

  // Load presets and measures on mount
  useEffect(() => {
    Promise.all([
      api.get('/quality-registry/presets'),
      api.get('/quality/dashboard/by-measure'),
    ]).then(([presetRes, measureRes]) => {
      setPresets(presetRes.data.presets || []);
      const m = measureRes.data.by_measure || {};
      setMeasures(Object.entries(m).map(([id, data]) => ({ id, name: data.name || id })));
    }).catch(() => {});
  }, []);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activePreset) params.append('preset_id', activePreset);
      if (statusFilter) params.append('status', statusFilter);
      if (measureFilter) params.append('measure_id', measureFilter);
      params.append('limit', limit);
      params.append('offset', page * limit);
      const res = await api.get(`/quality-registry/results?${params}`);
      setResults(res.data.results || []);
      setTotal(res.data.total || 0);

      // Fetch patient names for display
      const pIds = [...new Set((res.data.results || []).map(r => r.patient_id).filter(Boolean))];
      const ptMap = { ...patients };
      for (const pid of pIds) {
        if (!ptMap[pid]) {
          try {
            const pRes = await api.get(`/patients/${pid}`);
            const pt = pRes.data.patient || pRes.data;
            ptMap[pid] = pt?.name ? `${pt.name.first} ${pt.name.last}` : pid.slice(0, 8);
          } catch { ptMap[pid] = pid.slice(0, 8); }
        }
      }
      setPatients(ptMap);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [activePreset, statusFilter, measureFilter, page]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === results.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(results.map(r => r.id)));
    }
  };

  const handleBulkOutreach = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      const tasks = results
        .filter(r => selected.has(r.id))
        .map(r => ({
          patient_id: r.patient_id,
          measure_id: r.measure_id,
          title: `Quality follow-up: ${r.measure_id?.replace('qm_', '').replace(/_/g, ' ')}`,
          reason_code: 'screening_due',
          priority: r.priority === 'high' ? 'important' : 'routine',
          source_type: 'registry_bulk',
        }));
      const res = await api.post('/quality-registry/bulk-outreach', { tasks });
      toast.success(`Created ${res.data.count} outreach tasks`);
      setSelected(new Set());
      fetchResults();
    } catch (err) {
      toast.error('Failed to create outreach tasks');
    } finally { setBulkLoading(false); }
  };

  return (
    <div className="p-4 max-w-6xl mx-auto space-y-4" data-testid="quality-registry-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-primary" /> Quality Registry
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {total} records · {selected.size} selected
          </p>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button
              size="sm"
              onClick={handleBulkOutreach}
              disabled={bulkLoading}
              data-testid="registry-bulk-outreach"
            >
              <Send className="w-3.5 h-3.5 mr-1" />
              {bulkLoading ? 'Creating...' : `Create ${selected.size} Outreach Tasks`}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={fetchResults} data-testid="registry-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Preset Chips */}
      <div className="flex flex-wrap gap-1.5">
        <Button
          variant={!activePreset ? 'default' : 'outline'}
          size="sm"
          className="text-xs h-7"
          onClick={() => { setActivePreset(''); setPage(0); }}
          data-testid="registry-preset-all"
        >
          All Records
        </Button>
        {presets.map(p => (
          <Button
            key={p.id}
            variant={activePreset === p.id ? 'default' : 'outline'}
            size="sm"
            className="text-xs h-7"
            onClick={() => { setActivePreset(p.id); setStatusFilter(''); setMeasureFilter(''); setPage(0); }}
            data-testid={`registry-preset-${p.id}`}
          >
            {p.name}
          </Button>
        ))}
      </div>

      {/* Additional Filters */}
      <Card className="border">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-muted-foreground shrink-0" />
            <Select value={statusFilter || 'all'} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="registry-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="due">Due</SelectItem>
                <SelectItem value="compliant">Compliant</SelectItem>
                <SelectItem value="unknown">Review Needed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={measureFilter || 'all'} onValueChange={v => { setMeasureFilter(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="registry-measure-filter">
                <SelectValue placeholder="All Measures" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Measures</SelectItem>
                {measures.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results Table */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
        </div>
      ) : results.length === 0 ? (
        <Card className="border">
          <CardContent className="p-8 text-center">
            <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-40" />
            <p className="text-sm text-muted-foreground">No quality records match your filters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[32px_1fr_1fr_100px_80px_80px_60px] gap-2 px-3 py-2 bg-muted/40 border-b text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            <div className="flex items-center">
              <Checkbox
                checked={selected.size === results.length && results.length > 0}
                onCheckedChange={toggleAll}
                data-testid="registry-select-all"
              />
            </div>
            <div>Patient</div>
            <div>Measure</div>
            <div>Status</div>
            <div>Priority</div>
            <div>Tasks</div>
            <div></div>
          </div>

          {/* Table Rows */}
          {results.map(row => (
            <div
              key={row.id}
              className="grid grid-cols-[32px_1fr_1fr_100px_80px_80px_60px] gap-2 px-3 py-2.5 border-b last:border-0 hover:bg-muted/20 transition-colors items-center"
              data-testid={`registry-row-${row.id}`}
            >
              <div className="flex items-center">
                <Checkbox
                  checked={selected.has(row.id)}
                  onCheckedChange={() => toggleSelect(row.id)}
                  data-testid={`registry-check-${row.id}`}
                />
              </div>
              <div>
                <button
                  className="text-sm font-medium text-left hover:text-primary hover:underline"
                  onClick={() => row.patient_id && navigate(`/chart/${row.patient_id}`)}
                  data-testid={`registry-patient-${row.id}`}
                >
                  {patients[row.patient_id] || row.patient_id?.slice(0, 8)}
                </button>
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {row.measure_id?.replace('qm_', '').replace(/_/g, ' ')}
              </div>
              <div>
                <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${STATUS_COLORS[row.status] || 'bg-slate-50 text-slate-600'}`}>
                  {STATUS_ICONS[row.status]} {row.status}
                </span>
              </div>
              <div>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                  row.priority === 'high' ? 'bg-red-50 text-red-600' :
                  row.priority === 'medium' ? 'bg-amber-50 text-amber-600' :
                  'bg-slate-50 text-slate-500'
                }`}>
                  {row.priority}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                {row.open_outreach_task_count > 0 ? (
                  <span className="text-blue-600 font-medium">{row.open_outreach_task_count} open</span>
                ) : '—'}
              </div>
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => row.patient_id && navigate(`/chart/${row.patient_id}`)}
                  data-testid={`registry-goto-${row.id}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="registry-prev">Prev</Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)} data-testid="registry-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
